import React, { useMemo } from 'react'
import { Box, CircularProgress } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { useHistory } from 'react-router-dom'
import AuthService from 'tools/AuthService'
import { useUnderwriterDashboard } from './hooks/useUnderwriterDashboard'
import './UnderwriterDashboard.scss'
import UDHeader from './sections/UDHeader'
import UDDecisionedRow from './sections/UDDecisionedRow'
import UDPendingRow from './sections/UDPendingRow'
import UDStatCards from './sections/UDStatCards'
import UDLeaderboard from './sections/UDLeaderboard'

const useStyles = makeStyles((theme) => ({
    root: { padding: theme.spacing(3) },
    dateLabel: { fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--neutral-400)', textTransform: 'uppercase', marginBottom: 4 },
    greetingTitle: { fontSize: 32, fontWeight: 700, color: 'var(--neutral-900)', lineHeight: 1.2, marginBottom: 6, '& span': { color: 'var(--link)' } },
    greetingSubtitle: { fontSize: 14, color: 'var(--fg-tertiary)', '& strong': { color: 'var(--neutral-900)', fontWeight: 600 } },
    timeTabs: { display: 'flex', gap: 4, alignItems: 'center' },
    timeTab: { padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer', color: 'var(--fg-tertiary)', border: 'none', background: 'none', transition: 'all 0.15s', '&:hover': { color: 'var(--neutral-900)' } },
    timeTabActive: { background: 'var(--neutral-900)', color: '#fff !important', fontWeight: 600 },
    card: { backgroundColor: '#fff', borderRadius: 16, boxShadow: '0px 2px 12px rgba(0,0,0,0.06)', padding: theme.spacing(3), height: '100%' },
    darkCard: { backgroundColor: 'var(--neutral-900)', borderRadius: 16, boxShadow: '0px 2px 12px rgba(0,0,0,0.18)', padding: theme.spacing(3), height: '100%', color: '#fff' },
    sectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--neutral-400)', marginBottom: theme.spacing(1.5) },
    bigNumber: { fontSize: 48, fontWeight: 800, color: 'var(--neutral-900)', lineHeight: 1, display: 'inline' },
    bigNumberLight: { fontSize: 22, fontWeight: 400, color: 'var(--neutral-400)', marginLeft: 4 },
    bigNumberDark: { fontSize: 48, fontWeight: 800, color: '#fff', lineHeight: 1, display: 'inline' },
    bigNumberDarkUnit: { fontSize: 22, fontWeight: 400, color: '#8892a4', marginLeft: 4 },
    descText: { fontSize: 13, color: 'var(--fg-tertiary)', marginTop: 8, lineHeight: 1.5 },
    descTextDark: { fontSize: 13, color: '#8892a4', marginTop: 8, lineHeight: 1.5 },
    greenChip: { backgroundColor: 'var(--badge-approved-bg)', color: 'var(--status-approved)', fontSize: 12, fontWeight: 600, height: 26, borderRadius: 6 },
    redChip: { backgroundColor: 'var(--badge-declined-bg)', color: 'var(--badge-declined-ink)', fontSize: 12, fontWeight: 600, height: 26, borderRadius: 6 },
    grayChip: { backgroundColor: '#f0f2f5', color: 'var(--fg-tertiary)', fontSize: 12, fontWeight: 500, height: 26, borderRadius: 6 },
    tableHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2) },
    tableTitle: { fontSize: 16, fontWeight: 700, color: 'var(--neutral-900)' },
    tableSubtitle: { fontSize: 12, color: 'var(--neutral-400)' },
    appRow: { display: 'flex', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid #f0f2f5', '&:last-child': { borderBottom: 'none' } },
    appName: { fontSize: 14, fontWeight: 600, color: 'var(--neutral-900)' },
    appMeta: { fontSize: 11, color: 'var(--neutral-400)', letterSpacing: '0.04em', textTransform: 'uppercase', marginTop: 1 },
    statusDot: { width: 8, height: 8, borderRadius: '50%', display: 'inline-block', marginRight: 6 },
    statusLabel: { fontSize: 12, color: 'var(--fg-tertiary)', fontWeight: 500 },
    checksLabel: { fontSize: 11, color: 'var(--neutral-400)', marginBottom: 3 },
    pctLabel: { fontSize: 13, fontWeight: 600, color: 'var(--neutral-900)', minWidth: 36, textAlign: 'right' },
    openBtn: { border: '1px solid #e5e7eb', borderRadius: 8, padding: '5px 14px', fontSize: 13, fontWeight: 500, color: 'var(--neutral-900)', textTransform: 'none', minWidth: 80, '&:hover': { background: 'var(--bg-canvas)' } },
    agendaItem: { marginBottom: theme.spacing(2) },
    agendaTitle: { fontSize: 13, fontWeight: 600, color: 'var(--neutral-900)', marginBottom: 1 },
    agendaSub: { fontSize: 12, color: 'var(--neutral-400)' },
    agendaTag: { display: 'inline-block', marginTop: 4, padding: '2px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
    statCard: { backgroundColor: '#fff', borderRadius: 16, boxShadow: '0px 2px 12px rgba(0,0,0,0.06)', padding: theme.spacing(2.5), height: '100%' },
    statLabel: { fontSize: 13, color: 'var(--fg-tertiary)', fontWeight: 500, marginBottom: theme.spacing(1) },
    statValue: { fontSize: 36, fontWeight: 800, color: 'var(--neutral-900)', lineHeight: 1, display: 'inline' },
    statUnit: { fontSize: 15, color: 'var(--neutral-400)', fontWeight: 400, marginLeft: 3 },
    statSub: { fontSize: 12, color: 'var(--neutral-400)', marginTop: 4 },
    statChange: { fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 },
    statIcon: { display: 'flex', alignItems: 'center', color: 'var(--fg-tertiary)', '& svg': { fontSize: 20 } },
    statHead: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: theme.spacing(1) },
    statNoData: { fontSize: 20, fontWeight: 600, color: 'var(--fg-tertiary)', lineHeight: 1.2 },
    teamSection: { marginTop: theme.spacing(3), backgroundColor: '#fff', borderRadius: 16, boxShadow: '0px 2px 12px rgba(0,0,0,0.06)', padding: theme.spacing(3) },
    teamHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing(2) },
    teamTitle: { fontSize: 16, fontWeight: 700, color: 'var(--neutral-900)' },
    teamFilter: { fontSize: 12, color: 'var(--neutral-400)' },
    rankCard: { borderRadius: 14, padding: theme.spacing(2), height: '100%' },
    rankCardLight: { backgroundColor: '#fff', boxShadow: '0px 2px 8px rgba(0,0,0,0.05)' },
    rankCardDark: { backgroundColor: 'var(--neutral-900)' },
    rankLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 },
    rankLabelLight: { color: 'var(--neutral-400)' },
    rankLabelDark: { color: '#8892a4' },
    rankAvatar: { width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, marginRight: 10, flexShrink: 0 },
    rankName: { fontSize: 14, fontWeight: 600 },
    rankRole: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 1 },
    rankStats: { marginTop: theme.spacing(1.5), display: 'flex', gap: theme.spacing(2) },
    rankStatVal: { fontSize: 24, fontWeight: 800, lineHeight: 1 },
    rankStatLabel: { fontSize: 10, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', marginTop: 2 },
    streakDots: { display: 'flex', gap: 4, marginTop: theme.spacing(2), flexWrap: 'wrap' },
    streakDot: { width: 14, height: 6, borderRadius: 3, backgroundColor: '#7B61FF' },
    streakDotFaded: { width: 14, height: 6, borderRadius: 3, backgroundColor: '#2e3548' },
    loadingWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' },
    emptyText: { color: 'var(--neutral-400)', fontSize: 13, padding: '12px 0' }
}))

const UnderwriterDashboard = () => {
    const classes = useStyles()
    const history = useHistory()
    const { data, loading, range, setRange } = useUnderwriterDashboard('week')

    const loggedInUser = AuthService.loggedInUser || {}
    const firstName = loggedInUser.firstName || 'there'

    const now = useMemo(() => (data?.serverTime ? new Date(data.serverTime) : new Date()), [data])
    const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).toUpperCase()
    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })

    if (loading && !data) {
        return (
            <Box className={classes.root}>
                <Box className={classes.loadingWrap}><CircularProgress style={{ color: '#7B61FF' }} /></Box>
            </Box>
        )
    }

    const decisioned = data?.decisioned || { total: 0, approved: 0, declined: 0, cancelled: 0, delta: 0 }
    const streak = data?.streak || { days: 0 }
    const pending = data?.pendingApplications || []
    const agenda = data?.agenda || []
    const stats = data?.stats || {}
    const leaderboard = data?.leaderboard || []

    const slaHours = decisioned.slaHours
    const totalDecisioned = decisioned.total || 0
    const withinSla = decisioned.withinSla || 0
    const goal = totalDecisioned
    const goalPct = totalDecisioned === 0
        ? 0
        : Math.round((decisioned.slaHitRate != null ? decisioned.slaHitRate : (withinSla / totalDecisioned)) * 100)
    const remaining = decisioned.outsideSla || 0
    const allClear = totalDecisioned === 0
    const decisionedBigNumber = totalDecisioned === 0 ? 0 : withinSla

    return (
        <Box className={classes.root}>
            <UDHeader
                dateStr={dateStr}
                timeStr={timeStr}
                firstName={firstName}
                pending={pending}
                stats={stats}
                range={range}
                setRange={setRange}
                classes={classes}
            />
            <UDDecisionedRow
                decisioned={decisioned}
                bigNumber={decisionedBigNumber}
                goal={goal}
                goalPct={goalPct}
                remaining={remaining}
                allClear={allClear}
                slaHours={slaHours}
                streak={streak}
                range={range}
                classes={classes}
            />
            <UDPendingRow
                pending={pending}
                agenda={agenda}
                stats={stats}
                classes={classes}
                history={history}
            />
            <UDStatCards
                stats={stats}
                pending={pending}
                range={range}
                slaHours={slaHours}
                classes={classes}
            />
            <UDLeaderboard
                leaderboard={leaderboard}
                classes={classes}
            />
        </Box>
    )
}

export default UnderwriterDashboard
